import React, { useState, useEffect, useRef } from 'react';
import { createConversation, sendMessage } from '../utils/chatApi';
import { marked } from 'marked';

interface Message {
  role: 'user' | 'assistant';
  content: string;
}

interface ChatInterfaceProps {
  dayNumber: number;
  companyGameId?: number;
  isSpecialDay?: boolean;
  onTranscriptUpdate?: (transcript: string) => void;
  disabled?: boolean;
  messages?: Message[];
  conversationId?: number | null;
  onMessagesUpdate?: (messages: Message[]) => void;
  onConversationIdUpdate?: (conversationId: number) => void;
}

const ChatInterface: React.FC<ChatInterfaceProps> = ({
  dayNumber,
  companyGameId,
  isSpecialDay = false,
  onTranscriptUpdate,
  disabled = false,
  messages: externalMessages,
  conversationId: externalConversationId,
  onMessagesUpdate,
  onConversationIdUpdate
}) => {
  const [conversationId, setConversationId] = useState<number | null>(externalConversationId || null);
  const [messages, setMessages] = useState<Message[]>(externalMessages || []);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isInitializing, setIsInitializing] = useState(true);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Auto-scroll to bottom when messages change
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Sync with external messages
  useEffect(() => {
    if (externalMessages) {
      setMessages(externalMessages);
    }
  }, [externalMessages]);

  // Sync with external conversationId
  useEffect(() => {
    if (externalConversationId !== undefined) {
      setConversationId(externalConversationId);
    }
  }, [externalConversationId]);

  // Auto-resize textarea based on content
  useEffect(() => {
    const textarea = inputRef.current;
    if (textarea) {
      // Reset height to auto to get the correct scrollHeight
      textarea.style.height = 'auto';
      // Set height to scrollHeight, respecting min and max heights
      const newHeight = Math.min(Math.max(textarea.scrollHeight, 48), 120);
      textarea.style.height = `${newHeight}px`;
    }
  }, [inputValue]);

  // Update parent component with transcript whenever messages change
  useEffect(() => {
    if (onTranscriptUpdate) {
      const transcript = messages
        .map(msg => `${msg.role === 'user' ? 'User' : 'Assistant'}: ${msg.content}`)
        .join('\n\n');
      onTranscriptUpdate(transcript);
    }
  }, [messages, onTranscriptUpdate]);

  // Initialize conversation on mount if not already exists
  useEffect(() => {
    const initConversation = async () => {
      // Skip if conversation already exists
      if (conversationId) {
        setIsInitializing(false);
        return;
      }

      try {
        setIsInitializing(true);
        setError(null);
        const convId = await createConversation(dayNumber, companyGameId);
        setConversationId(convId);
        if (onConversationIdUpdate) {
          onConversationIdUpdate(convId);
        }
      } catch (err) {
        console.error('Failed to initialize conversation:', err);
        setError('Failed to initialize chat. Please refresh and try again.');
      } finally {
        setIsInitializing(false);
      }
    };

    initConversation();
  }, [dayNumber, companyGameId, conversationId, onConversationIdUpdate]);

  // Handle sending a message
  const handleSendMessage = async () => {
    if (!inputValue.trim() || isLoading || !conversationId) {
      return;
    }

    const userMessage = inputValue.trim();
    setInputValue('');
    setError(null);

    // Add user message immediately
    const userMsg: Message = { role: 'user', content: userMessage };
    const newMessages = [...messages, userMsg];
    setMessages(newMessages);
    if (onMessagesUpdate) {
      onMessagesUpdate(newMessages);
    }

    // Show loading state
    setIsLoading(true);

    try {
      // Send message and get response
      const assistantResponse = await sendMessage(conversationId, userMessage);

      // Add assistant response
      const assistantMsg: Message = { role: 'assistant', content: assistantResponse };
      const updatedMessages = [...messages, userMsg, assistantMsg];
      setMessages(updatedMessages);
      if (onMessagesUpdate) {
        onMessagesUpdate(updatedMessages);
      }
    } catch (err) {
      console.error('Failed to send message:', err);
      setError('Failed to send message. Please try again.');
      // Remove the user message that failed
      const revertedMessages = messages.slice(0, -1);
      setMessages(revertedMessages);
      if (onMessagesUpdate) {
        onMessagesUpdate(revertedMessages);
      }
      // Restore input value
      setInputValue(userMessage);
    } finally {
      setIsLoading(false);
      // Focus back on input
      inputRef.current?.focus();
    }
  };

  // Handle Enter key press
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Render markdown for assistant messages
  const renderMessage = (msg: Message) => {
    if (msg.role === 'assistant') {
      const htmlContent = marked(msg.content) as string;
      return (
        <div
          className="chat-message-content"
          dangerouslySetInnerHTML={{ __html: htmlContent }}
        />
      );
    }
    return <div className="chat-message-content">{msg.content}</div>;
  };

  if (isInitializing) {
    return (
      <div className="chat-interface">
        <div className="chat-initializing">
          <div className="chat-spinner"></div>
          <span>Initializing chat...</span>
        </div>
      </div>
    );
  }

  if (error && !conversationId) {
    return (
      <div className="chat-interface">
        <div className="chat-error">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className={`chat-interface ${isSpecialDay ? 'special-day' : ''}`}>
      <div className="chat-messages-display">
        {messages.map((msg, index) => (
          <div key={index} className={`chat-message chat-message-${msg.role}`}>
            <div className="chat-message-role">
              {msg.role === 'user' ? 'You' : 'Assistant'}:
            </div>
            {renderMessage(msg)}
          </div>
        ))}
        {isLoading && (
          <div className="chat-message chat-message-loading">
            <div className="chat-message-role">Assistant:</div>
            <div className="chat-loading">
              <div className="chat-spinner"></div>
              <span>Thinking...</span>
            </div>
          </div>
        )}
        {error && conversationId && (
          <div className="chat-error-inline">
            {error}
          </div>
        )}
        <div ref={messagesEndRef} className="chat-scroll-anchor" />
      </div>

      <div className="chat-input-container">
        <textarea
          ref={inputRef}
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Enter AI Prompt..."
          className="chat-input"
          disabled={disabled || isLoading || !conversationId}
          rows={1}
        />
        <button
          onClick={handleSendMessage}
          disabled={disabled || isLoading || !conversationId || !inputValue.trim()}
          className={`chat-send-button ${isSpecialDay ? 'special-day' : ''}`}
        >
          Send
        </button>
      </div>
    </div>
  );
};

export default ChatInterface;
